import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, Calendar, FileText, Image, Download, RefreshCw, CheckCircle, LogOut } from 'lucide-react';
import axios from 'axios';

const API_URL = 'https://apiproblema.digitalbotia.com.br/api';

function AdminPanel() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
    }
  }, [navigate]);

  const fetchRequests = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_URL}/requests`);
      if (response.data.success) {
        setRequests(response.data.data.reverse()); // Mais recentes primeiro
      }
    } catch (err) {
      setError('Erro ao carregar solicitações. Verifique se o servidor está rodando.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileIcon = (mimeType) => {
    if (mimeType.startsWith('image/')) {
      return <Image className="w-5 h-5" />;
    }
    return <FileText className="w-5 h-5" />;
  };

  const openFile = (filename) => {
    window.open(`http://localhost:5000/uploads/${filename}`, '_blank');
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const handleMarkAsCompleted = async (requestId) => {
    setUpdatingStatus(requestId);
    try {
      const response = await axios.patch(`${API_URL}/requests/${requestId}/status`, {
        status: 'concluído'
      });
      
      if (response.data.success) {
        // Atualizar a lista de solicitações
        setRequests(prevRequests => 
          prevRequests.map(req => 
            req.id === requestId ? { ...req, status: 'concluído', dataAtualizacao: response.data.data.dataAtualizacao } : req
          )
        );
        
        // Atualizar solicitação selecionada se for a mesma
        if (selectedRequest && selectedRequest.id === requestId) {
          setSelectedRequest(response.data.data);
        }
      }
    } catch (err) {
      alert('Erro ao atualizar status: ' + (err.response?.data?.message || 'Tente novamente'));
    } finally {
      setUpdatingStatus(null);
    }
  };

  if (selectedRequest) {
    return (
      <div className="min-h-screen p-8">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => setSelectedRequest(null)}
            className="flex items-center gap-2 text-white mb-6 hover:opacity-80 transition-opacity"
          >
            <ArrowLeft className="w-5 h-5" />
            Voltar para lista
          </button>

          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              Detalhes da Solicitação
            </h2>

            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    Email de Licença
                  </label>
                  <div className="flex items-center gap-2 text-gray-800">
                    <Mail className="w-5 h-5 text-green-600" />
                    <span className="font-medium">{selectedRequest.email}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    Telefone
                  </label>
                  <div className="flex items-center gap-2 text-gray-800">
                    <Phone className="w-5 h-5 text-green-600" />
                    <span className="font-medium">{selectedRequest.telefone}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    Data de Envio
                  </label>
                  <div className="flex items-center gap-2 text-gray-800">
                    <Calendar className="w-5 h-5 text-green-600" />
                    <span className="font-medium">{formatDate(selectedRequest.dataEnvio)}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    Status
                  </label>
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      selectedRequest.status === 'concluído' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {selectedRequest.status}
                    </span>
                    {selectedRequest.status !== 'concluído' && (
                      <button
                        onClick={() => handleMarkAsCompleted(selectedRequest.id)}
                        disabled={updatingStatus === selectedRequest.id}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <CheckCircle className="w-4 h-4" />
                        {updatingStatus === selectedRequest.id ? 'Atualizando...' : 'Marcar como Concluído'}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-3">
                  Comprovante de Pagamento
                </label>
                <div className="border-2 border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {getFileIcon(selectedRequest.comprovanteTipo)}
                      <div>
                        <p className="font-medium text-gray-800">
                          {selectedRequest.comprovanteOriginal}
                        </p>
                        <p className="text-sm text-gray-500">
                          {selectedRequest.comprovanteTipo}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => openFile(selectedRequest.comprovante)}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Abrir
                    </button>
                  </div>

                  {selectedRequest.comprovanteTipo.startsWith('image/') && (
                    <div className="mt-4">
                      <img
                        src={`http://localhost:5000/uploads/${selectedRequest.comprovante}`}
                        alt="Comprovante"
                        className="w-full rounded-lg shadow-lg"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">
              Painel Administrativo
            </h1>
            <p className="text-green-400">
              <span className="text-green-400 font-bold">OpenBot</span> - Gerenciamento de solicitações de restauração de licença
            </p>
          </div>
          <div className="flex gap-4">
            <button
              onClick={fetchRequests}
              className="flex items-center gap-2 px-4 py-2 bg-white text-green-600 rounded-lg hover:bg-green-50 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Atualizar
            </button>
            <a
              href="/"
              className="flex items-center gap-2 px-4 py-2 bg-white text-green-600 rounded-lg hover:bg-green-50 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Página Inicial
            </a>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Sair
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="bg-white rounded-2xl shadow-2xl p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Carregando solicitações...</p>
          </div>
        ) : requests.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-2xl p-12 text-center">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              Nenhuma solicitação encontrada
            </h3>
            <p className="text-gray-600">
              As solicitações enviadas pelos clientes aparecerão aqui.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800">
                Total de Solicitações: {requests.length}
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data/Hora
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Telefone
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Comprovante
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {requests.map((request) => (
                    <tr key={request.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(request.dataEnvio)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-800">{request.email}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-800">{request.telefone}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => openFile(request.comprovante)}
                          className="flex items-center gap-2 text-green-600 hover:text-green-700 text-sm"
                        >
                          {getFileIcon(request.comprovanteTipo)}
                          Ver arquivo
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          request.status === 'concluído' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {request.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setSelectedRequest(request)}
                            className="text-green-600 hover:text-green-700 font-medium"
                          >
                            Ver detalhes
                          </button>
                          {request.status !== 'concluído' && (
                            <>
                              <span className="text-gray-300">|</span>
                              <button
                                onClick={() => handleMarkAsCompleted(request.id)}
                                disabled={updatingStatus === request.id}
                                className="text-green-600 hover:text-green-700 font-medium disabled:opacity-50"
                              >
                                {updatingStatus === request.id ? 'Atualizando...' : 'Concluir'}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminPanel;
